<template>
  <div v-if="files.length || inlineFiles.length" class="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
    <h4 class="text-sm font-medium text-blue-800 mb-2">
      📎 Attachments ({{ files.length }})
    </h4>
    <ul class="space-y-2">
      <li v-for="file in files" :key="file.id"
          class="flex items-center justify-between bg-white p-2 rounded border">
        <div class="flex items-center min-w-0">
          <span class="text-gray-600 mr-2">{{ icon(file.content_type) }}</span>
          <div class="min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate">{{ file.filename }}</div>
            <div class="text-xs text-gray-500">
              {{ file.content_type }}<span v-if="file.size"> • {{ formatSize(file.size) }}</span>
            </div>
            <div v-if="!file.available" class="text-xs text-red-600">
              Not stored{{ file.error ? `: ${file.error}` : '' }}
            </div>
          </div>
        </div>
        <button
          v-if="file.available"
          class="ml-3 shrink-0 px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          :disabled="busy === file.id"
          @click="open(file)"
        >
          {{ busy === file.id ? 'Opening…' : 'Open' }}
        </button>
      </li>
    </ul>
    <details v-if="inlineFiles.length" class="mt-2 text-xs text-gray-600">
      <summary class="cursor-pointer">{{ inlineFiles.length }} inline image(s) from the signature</summary>
      <ul class="mt-1 space-y-1">
        <li v-for="file in inlineFiles" :key="file.id">
          <button v-if="file.available" class="text-blue-700 hover:underline" @click="open(file)">{{ file.filename }}</button>
          <span v-else>{{ file.filename }} (not stored)</span>
        </li>
      </ul>
    </details>
    <p v-if="error" class="mt-2 text-xs text-red-600">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import apiClient from '@/api/client'

export interface ReplyAttachment {
  id: string
  filename: string
  content_type: string
  size?: number | null
  is_inline: boolean
  available: boolean
  error?: string | null
}

const props = defineProps<{ attachments?: ReplyAttachment[] | null }>()

const files = computed(() => (props.attachments || []).filter(a => !a.is_inline))
const inlineFiles = computed(() => (props.attachments || []).filter(a => a.is_inline))
const busy = ref<string | null>(null)
const error = ref<string | null>(null)

// The download needs the admin bearer token, so it goes through apiClient as a blob
// instead of a plain link. PDFs and images open in a new tab; anything else downloads.
async function open(file: ReplyAttachment) {
  busy.value = file.id
  error.value = null
  // Opened before the await, so the popup blocker treats it as part of the click.
  const viewable = file.content_type === 'application/pdf' || file.content_type.startsWith('image/')
  const tab = viewable ? window.open('', '_blank') : null
  try {
    const response = await apiClient.get(`/api/emails/replies/attachments/${file.id}`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([response.data], { type: file.content_type }))
    if (tab) {
      tab.location.href = url
    } else {
      const link = document.createElement('a')
      link.href = url
      link.download = file.filename
      link.click()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e: any) {
    tab?.close()
    error.value = `Could not open ${file.filename}: ${e?.response?.status === 404 ? 'file not stored' : e?.message || 'error'}`
  } finally {
    busy.value = null
  }
}

function icon(contentType: string) {
  if (contentType.startsWith('image/')) return '🖼️'
  if (contentType.includes('pdf')) return '📄'
  if (contentType.includes('word') || contentType.includes('doc')) return '📝'
  if (contentType.includes('excel') || contentType.includes('sheet')) return '📊'
  if (contentType.includes('zip') || contentType.includes('compressed')) return '🗜️'
  return '📎'
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>
